import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux';

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent,
    IconButton,
    Checkbox,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Chip,
    Typography,
    InputAdornment,
    Pagination,
    Stack,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TextFieldGroup from '../common/TextFieldGroup'
import Modal from '../common/Modal'
import translate from '../../i18n/translate';

import { updateActiveProfile, updateDiscountProfile } from '../../actions/userAction'

const UserCompanies = ({ discountData,
    setDiscountData,
    isDarktheme,
    seller,
    getDiscounts,
    updateData,
    updateActiveProfile,
    updateDiscountProfile
}) => {

    const [editDiscountActiveModal, setEditDiscountActiveModal] = useState(false)
    const [updateDiscountLoading, setUpdateDiscountActiveLoading] = useState(false)
    const [activeEditData, setActiveEditData] = useState({
        company: '',
        active: '',
        id: ''
    })
    const [discountEditData, setDiscountEditData] = useState({
        company: '',
        discount: '',
        id: ''
    })

    const [currentPageContent, setCurrentPageContent] = useState([]);
    const [buttons, setButtons] = useState([]);
    const [selectedButtom, setSelectedButtom] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [filteredDiscounts, setFilteredDiscounts] = useState([]);
    const itemsPerPage = 10;

    useEffect(() => {
        if (discountData.length) {
            const filtered = discountData.filter(item =>
                item.product?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredDiscounts(filtered);
            setCurrentPage(1);
        }
    }, [discountData, searchTerm]);

    useEffect(() => {
        if (filteredDiscounts.length) {
            getPageContent(currentPage, filteredDiscounts);
            getPagesNumbers(filteredDiscounts);
        }
    }, [filteredDiscounts, currentPage])

    const OpenEditModal = (company, discount, id, active) => {
        setDiscountEditData({
            company,
            discount,
            id
        })

        setActiveEditData({
            company,
            active: active === 'true' ? true : false,
            id
        })
        setEditDiscountActiveModal(true)
    }

    const handleDiscountUpdate = async (setUpdateLoading) => {
        setUpdateLoading(true)

        let data = {
            ...seller,
            company: discountEditData.company,
            newDiscount: discountEditData.discount,
            newActive: activeEditData.active
        }

        await updateDiscountProfile(data)
        await updateActiveProfile(data)
    }

    const getPageContent = (pageNumber, data) => {
        const temp = data.length > 0 ? [...data] : []
        let currentContent = temp?.slice(pageNumber * 10 - 10, pageNumber * 10)
        setCurrentPageContent([...currentContent])
    }

    const getPagesNumbers = (data) => {
        let pagesCount = Math.ceil(data?.length / 10);

        let buttons = []
        for (let i = 1; i <= pagesCount; i++) {
            buttons.push({ index: i })
        }
        setButtons(buttons)
    }

    const totalPages = Math.ceil(filteredDiscounts.length / itemsPerPage);

    return (
        <Box sx={{ mt: 3 }}>
            <Card 
                className="discount-table-card"
                sx={{ 
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    background: isDarktheme ? '#2d3748' : 'white',
                    overflow: 'visible'
                }}
            >
                <CardContent sx={{ padding: '20px !important' }}>
                    {/* Header Section */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2
                    }}>
                        <Box>
                            <Typography 
                                variant="h5" 
                                sx={{ 
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    fontSize: '22px',
                                    mb: 0.5
                                }}
                            >
                                {translate("Company Discounts")}
                            </Typography>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: isDarktheme ? '#a0aec0' : '#718096',
                                    fontSize: '13px'
                                }}
                            >
                                {filteredDiscounts.length} {translate("companies")}
                            </Typography>
                        </Box>
                        
                        {/* Search Field */}
                        <TextField
                            size="small"
                            placeholder={translate("Search companies...")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#667eea' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                minWidth: '250px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: isDarktheme ? '#1a202c' : '#f8fafc',
                                    '&:hover fieldset': {
                                        borderColor: '#667eea',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#667eea',
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* Table */}
                    <TableContainer sx={{ 
                        borderRadius: '10px',
                        border: isDarktheme ? '1px solid #4a5568' : '1px solid #e2e8f0'
                    }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ 
                                    background: isDarktheme 
                                        ? 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)' 
                                        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
                                }}>
                                    <TableCell 
                                        align="left"
                                        sx={{ 
                                            fontWeight: 700,
                                            color: isDarktheme ? '#f7fafc' : '#1a202c',
                                            fontSize: '13px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '14px 16px'
                                        }}
                                    >
                                        {translate("Company")}
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{ 
                                            fontWeight: 700,
                                            color: isDarktheme ? '#f7fafc' : '#1a202c',
                                            fontSize: '13px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        {translate("Discount")}
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{ 
                                            fontWeight: 700,
                                            color: isDarktheme ? '#f7fafc' : '#1a202c',
                                            fontSize: '13px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        {translate("Status")}
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{ 
                                            fontWeight: 700,
                                            color: isDarktheme ? '#f7fafc' : '#1a202c',
                                            fontSize: '13px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        {translate("Actions")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {currentPageContent.length > 0 ? (
                                    currentPageContent.map((discount, i) => (
                                        <TableRow
                                            key={i.toString()}
                                            sx={{ 
                                                '&:hover': {
                                                    backgroundColor: isDarktheme ? '#1a202c' : '#f8fafc',
                                                    transition: 'background-color 0.2s ease'
                                                },
                                                '&:last-child td': { 
                                                    borderBottom: 0 
                                                }
                                            }}
                                        >
                                            <TableCell 
                                                align="left"
                                                sx={{ 
                                                    color: isDarktheme ? '#f7fafc' : '#1a202c',
                                                    fontWeight: 500,
                                                    fontSize: '14px',
                                                    padding: '14px 16px'
                                                }}
                                            >
                                                {discount.product}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={`${discount.discount}%`}
                                                    size="small"
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        fontSize: '13px',
                                                        minWidth: '60px'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                {discount.active === 'true' ? (
                                                    <Chip
                                                        icon={<CheckCircleIcon sx={{ color: 'white !important' }} />}
                                                        label={translate("Active")}
                                                        size="small"
                                                        sx={{
                                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                            color: 'white',
                                                            fontWeight: 600,
                                                            fontSize: '12px'
                                                        }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<CancelIcon sx={{ color: 'white !important' }} />}
                                                        label={translate("Inactive")}
                                                        size="small"
                                                        sx={{
                                                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                            color: 'white',
                                                            fontWeight: 600,
                                                            fontSize: '12px'
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => OpenEditModal(discount.product, discount.discount, discount.id, discount.active)}
                                                    sx={{
                                                        color: '#667eea',
                                                        backgroundColor: isDarktheme ? 'rgba(129, 140, 248, 0.1)' : '#f0f4ff',
                                                        '&:hover': {
                                                            backgroundColor: isDarktheme ? 'rgba(129, 140, 248, 0.2)' : '#e0e7ff',
                                                            transform: 'scale(1.1)',
                                                        },
                                                        transition: 'all 0.2s ease',
                                                        padding: '8px'
                                                    }}
                                                >
                                                    <EditIcon sx={{ fontSize: '18px' }} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <Typography sx={{ color: isDarktheme ? '#a0aec0' : '#718096' }}>
                                                {translate("No companies found")}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Modern Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            mt: 3 
                        }}>
                            <Stack spacing={2}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={(e, page) => setCurrentPage(page)}
                                    color="primary"
                                    shape="rounded"
                                    size="medium"
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            fontWeight: 600,
                                            color: isDarktheme ? '#f7fafc' : '#1a202c',
                                            '&.Mui-selected': {
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                            },
                                            '&:hover': {
                                                backgroundColor: isDarktheme ? '#1a202c' : '#f0f4ff',
                                            },
                                        },
                                    }}
                                />
                            </Stack>
                        </Box>
                    )}
                </CardContent>
            </Card>
            {/* Modern Edit Dialog */}
            <Dialog
                open={editDiscountActiveModal}
                onClose={() => setEditDiscountActiveModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: isDarktheme ? '#2d3748' : 'white',
                    }
                }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '20px',
                    padding: '20px 24px'
                }}>
                    {translate("Edit Discount")}
                </DialogTitle>
                <DialogContent sx={{ padding: '24px', mt: 2 }}>
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 3,
                                color: isDarktheme ? '#f7fafc' : '#1a202c',
                                fontWeight: 600,
                                fontSize: '16px'
                            }}
                        >
                            {discountEditData.company}
                        </Typography>

                        <TextField
                            fullWidth
                            required
                            type="number"
                            label={translate("Discount Percentage")}
                            value={discountEditData.discount}
                            onChange={(e) => setDiscountEditData({
                                ...discountEditData,
                                discount: e.target.value
                            })}
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: isDarktheme ? '#1a202c' : '#f8fafc',
                                    '&:hover fieldset': {
                                        borderColor: '#667eea',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#667eea',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: isDarktheme ? '#a0aec0' : '#4a5568',
                                },
                                '& .MuiInputBase-input': {
                                    color: isDarktheme ? '#f7fafc' : '#1a202c',
                                },
                            }}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                        />

                        <FormControl 
                            component="fieldset"
                            sx={{
                                width: '100%',
                                padding: '16px',
                                borderRadius: '8px',
                                backgroundColor: isDarktheme ? '#1a202c' : '#f8fafc',
                                border: isDarktheme ? '1px solid #4a5568' : '1px solid #e2e8f0'
                            }}
                        >
                            <FormLabel 
                                component="legend"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    color: isDarktheme ? '#f7fafc' : '#1a202c',
                                    mb: 1,
                                    '&.Mui-focused': {
                                        color: isDarktheme ? '#f7fafc' : '#1a202c',
                                    }
                                }}
                            >
                                {translate("Active Status")}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={activeEditData.active ? 'on' : 'off'}
                                onChange={(e) => setActiveEditData({
                                    ...activeEditData,
                                    active: e.target.value === 'on'
                                })}
                                sx={{
                                    gap: 2
                                }}
                            >
                                <FormControlLabel
                                    value="on"
                                    control={
                                        <Radio 
                                            sx={{
                                                color: isDarktheme ? '#a0aec0' : '#4a5568',
                                                '&.Mui-checked': {
                                                    color: '#10b981',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CheckCircleIcon sx={{ fontSize: '18px', color: '#10b981' }} />
                                            <Typography sx={{
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                color: isDarktheme ? '#f7fafc' : '#1a202c',
                                            }}>
                                                {translate("ON")}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{
                                        padding: '8px 16px',
                                        margin: 0,
                                        borderRadius: '8px',
                                        border: activeEditData.active 
                                            ? '2px solid #10b981' 
                                            : isDarktheme ? '2px solid #4a5568' : '2px solid #e2e8f0',
                                        backgroundColor: activeEditData.active 
                                            ? 'rgba(16, 185, 129, 0.1)' 
                                            : 'transparent',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: activeEditData.active 
                                                ? 'rgba(16, 185, 129, 0.15)' 
                                                : isDarktheme ? 'rgba(129, 140, 248, 0.05)' : 'rgba(102, 126, 234, 0.05)',
                                        }
                                    }}
                                />
                                <FormControlLabel
                                    value="off"
                                    control={
                                        <Radio 
                                            sx={{
                                                color: isDarktheme ? '#a0aec0' : '#4a5568',
                                                '&.Mui-checked': {
                                                    color: '#ef4444',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CancelIcon sx={{ fontSize: '18px', color: '#ef4444' }} />
                                            <Typography sx={{
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                color: isDarktheme ? '#f7fafc' : '#1a202c',
                                            }}>
                                                {translate("OFF")}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{
                                        padding: '8px 16px',
                                        margin: 0,
                                        borderRadius: '8px',
                                        border: !activeEditData.active 
                                            ? '2px solid #ef4444' 
                                            : isDarktheme ? '2px solid #4a5568' : '2px solid #e2e8f0',
                                        backgroundColor: !activeEditData.active 
                                            ? 'rgba(239, 68, 68, 0.1)' 
                                            : 'transparent',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: !activeEditData.active 
                                                ? 'rgba(239, 68, 68, 0.15)' 
                                                : isDarktheme ? 'rgba(129, 140, 248, 0.05)' : 'rgba(102, 126, 234, 0.05)',
                                        }
                                    }}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px 20px' }}>
                    <Button
                        onClick={() => setEditDiscountActiveModal(false)}
                        variant="outlined"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderWidth: 2,
                            borderRadius: '8px',
                            color: isDarktheme ? '#f7fafc' : '#1a202c',
                            borderColor: isDarktheme ? '#4a5568' : '#e2e8f0',
                            '&:hover': {
                                borderWidth: 2,
                                borderColor: '#667eea',
                            },
                        }}
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        onClick={async () => {
                            await handleDiscountUpdate(setUpdateDiscountActiveLoading)
                            getDiscounts(true, (seller?.seller_id || seller?.id), setDiscountData)
                            setEditDiscountActiveModal(false)
                            setUpdateDiscountActiveLoading(false)
                        }}
                        variant="contained"
                        disabled={updateDiscountLoading}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                            },
                        }}
                    >
                        {updateDiscountLoading ? translate("Saving...") : translate("Save Changes")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

const mapStateToProps = ({ darkTheme: { isDarktheme }, auth: { user, userDetails } }) => ({
    isDarktheme,
    user,
    userDetails
});

export default connect(mapStateToProps, { updateActiveProfile, updateDiscountProfile })(UserCompanies)
